import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminTestimonials() {
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchTestimonials = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/testimonials?status=${filter}`, {
        withCredentials: true,
      });
      setTestimonials(res.data.testimonials || []);
    } catch (err) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchTestimonials(); }, [fetchTestimonials]);

  const handleUpdate = async (id, status) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/testimonials/${id}`,
        { status },
        { withCredentials: true }
      );
      toast.success(`Testimonial ${status}`);
      fetchTestimonials();
    } catch (err) {
      toast.error('Failed to update testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial permanently?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/testimonials/${id}`, {
        withCredentials: true,
      });
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (err) {
      toast.error('Failed to delete testimonial');
    }
  };

  const statusIcon = {
    pending: <Clock className="w-4 h-4 text-amber-400" />,
    approved: <CheckCircle className="w-4 h-4 text-emerald-400" />,
    rejected: <XCircle className="w-4 h-4 text-red-400" />,
  };

  const filters = ['all', 'pending', 'approved', 'rejected'];

  return (
    <div data-testid="admin-testimonials">
      <h1 className="font-space text-2xl text-white mb-6">Testimonials</h1>

      <div className="flex gap-2 mb-6">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-space font-bold transition-all ${
              filter === f
                ? 'shimmer-btn text-white border-0'
                : 'bg-white/[0.04] text-white/40 border border-white/[0.08] hover:text-white/60'
            }`}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-white/30" />
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-12 text-white/30">
          No {filter === 'all' ? '' : filter} testimonials yet.
        </div>
      ) : (
        <div className="space-y-4">
          {testimonials.map((t) => (
            <div key={t.id} className="bg-[var(--bg-elevated)] border border-white/[0.06] rounded-xl p-5" data-testid={`testimonial-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full pi-gradient flex items-center justify-center text-white text-xs font-bold">
                    {t.user_name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">{t.user_name}</p>
                    <p className="text-white/30 text-xs">{t.user_email} {t.role && `\u2022 ${t.role}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {statusIcon[t.status]}
                  <span className="text-white/40 text-xs capitalize">{t.status}</span>
                </div>
              </div>

              <p className="text-white/60 text-sm italic mb-4 leading-relaxed">"{t.quote}"</p>

              <div className="flex items-center justify-between">
                <p className="text-white/20 text-xs">{new Date(t.created_at).toLocaleDateString()}</p>
                <div className="flex gap-2">
                  {t.status !== 'approved' && (
                    <Button
                      onClick={() => handleUpdate(t.id, 'approved')}
                      size="sm"
                      className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 rounded-full px-4 text-xs"
                      data-testid={`approve-${t.id}`}
                    >
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                  )}
                  {t.status !== 'rejected' && (
                    <Button
                      onClick={() => handleUpdate(t.id, 'rejected')}
                      size="sm"
                      variant="outline"
                      className="border-red-500/20 text-red-400 hover:bg-red-500/10 rounded-full px-4 text-xs"
                      data-testid={`reject-${t.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  )}
                  <Button
                    onClick={() => handleDelete(t.id)}
                    size="sm"
                    variant="outline"
                    className="border-white/10 text-white/30 hover:bg-white/5 rounded-full px-3 text-xs"
                    data-testid={`delete-${t.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
